"use client";

import "react-international-phone/style.css";

import clsx from "clsx";
import React, { useEffect } from "react";
import {
  CountryData,
  PhoneInput as ReactPhoneInput,
  defaultCountries,
} from "react-international-phone";
import { SupportedCountries } from "@/enums/internationalization";
import { useProfile } from "@/hooks/useProfile";
import { GetPhoneNumberForDb } from "@/utils/common/phone";

interface PhoneInputProps {
  id?: string;
  value: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  helperText?: string;
  allowedCountries?: SupportedCountries[];
  setValue: (value: string) => void;
  setValid: (isValid: boolean) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  initialCountry?: SupportedCountries;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  initialCountry,
  label,
  placeholder,
  value,
  disabled,
  helperText,
  required,
  allowedCountries,
  setValue,
  setValid,
  onKeyDown = () => {},
  onBlur = () => {},
}) => {
  const { ui } = useProfile();

  const countriesAllowed = allowedCountries || ui.allowedCountries || [];

  const [country, setCountry] = React.useState<SupportedCountries>(
    initialCountry || ui.country || SupportedCountries.UnitedStates
  );

  useEffect(() => {
    if (initialCountry) {
      if (initialCountry !== country) {
        setCountry(initialCountry);
      }
    } else {
      if (ui.country !== country) {
        setCountry(ui.country);
      }
    }
  }, [initialCountry, ui.country]);

  useEffect(() => {
    const e164 = GetPhoneNumberForDb(value, country);

    if (!e164) {
      setValid(false);
      return;
    }

    if (value !== e164) {
      setValue(e164);
    }

    setValid(true);
  }, [value, country]);

  return (
    <div>
      <label className={clsx("block", "text-sm", "font-medium", "mb-1")}>
        {label}
        {required && <span className="ml-1">*</span>}
      </label>
      <ReactPhoneInput
        name={id ?? "phone"}
        defaultCountry={
          countriesAllowed.includes(country)
            ? country.toLowerCase()
            : countriesAllowed[0].toLowerCase()
        }
        onChange={setValue}
        value={value}
        countries={defaultCountries
          .filter((country) => {
            return countriesAllowed.includes(
              country[1].toUpperCase() as SupportedCountries
            );
          })
          .map((country) => {
            if (country[1] === "ar") {
              return [
                "Argentina",
                "ar",
                "54",
                {
                  default: ".. .... ....",
                  "/^9/": ". .. .... ....",
                  "/^8/": "... ... ....",
                  "/^6/": "... ... ....",
                },
                0,
              ] satisfies CountryData;
            } else {
              return country;
            }
          })}
        disabled={disabled}
        disableDialCodeAndPrefix={true}
        placeholder={placeholder}
        onBlur={onBlur}
        inputProps={{ onKeyDown: onKeyDown, id: id }}
        className={clsx(
          "rounded-lg",
          "border",
          "[&_button]:!rounded-l-md",
          "border-gray-300",
          "[&_button]:!bg-gray-200",
          "dark:border-gray-600",
          "dark:[&_button]:!bg-gray-600",
          "[&_button]:!py-5",
          "[&_button]:!pl-2",
          "[&_button]:!pr-1",
          "[&_button]:!border-l-0",
          "[&_button]:!border-t-0",
          "[&_button]:!border-b-0",
          "dark:[&_ul]:!bg-gray-700",
          "dark:[&_li]:!bg-gray-700",
          "dark:[&_li:hover]:!bg-gray-600"
        )}
        inputClassName={clsx(
          "!bg-white",
          "dark:!bg-gray_comp",
          "w-full",
          "!border-none",
          "!rounded-r-lg",
          "!leading-5",
          "!px-3",
          "!py-5",
          "!text-800",
          "focus:!ring-gray-300",
          "dark:focus:!ring-gray-700"
        )}
      />
      {helperText && (
        <p className={clsx("mt-2", "text-xs", "text-red-800")}>{helperText}</p>
      )}
    </div>
  );
};

export default PhoneInput;
